import React, { useState, useEffect, useCallback } from 'react';
import { X, Download, Loader2, CheckCircle, Shield } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'whd2026_contact';

const getStoredContact = () => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch { return null; }
};

const storeContact = (contact) => {
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(contact)); } catch { /* storage unavailable */ }
};

export const LeadCapturePopup = ({ open, onClose, resourceName = 'Event Brochure', onSuccess, downloadUrl }) => {
  const [step, setStep] = useState('form'); // form | submitting | success
  const [formData, setFormData] = useState({ full_name: '', email: '', whatsapp: '' });
  const [errors, setErrors] = useState({});
  const [isReturning, setIsReturning] = useState(false);
  const [contactId, setContactId] = useState(null);

  useEffect(() => {
    if (open) {
      const stored = getStoredContact();
      if (stored) {
        setFormData({ full_name: stored.full_name || '', email: stored.email || '', whatsapp: stored.whatsapp || '' });
        setIsReturning(true);
        setContactId(stored.contact_id);
      } else {
        setFormData({ full_name: '', email: '', whatsapp: '' });
        setIsReturning(false);
        setContactId(null);
      }
      setStep('form');
      setErrors({});
    }
  }, [open]);

  const validate = () => {
    const e = {};
    if (!formData.full_name.trim() || formData.full_name.trim().length < 2) e.full_name = 'Please enter your full name';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) e.email = 'Please enter a valid email';
    const cleaned = formData.whatsapp.replace(/[\s\-\(\)]/g, '');
    if (!/^\+?[0-9]{7,15}$/.test(cleaned)) e.whatsapp = 'Please enter a valid WhatsApp number';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setStep('submitting');

    try {
      const res = await fetch(`${BACKEND_URL}/api/contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: formData.full_name.trim(),
          email: formData.email.toLowerCase().trim(),
          whatsapp: formData.whatsapp.trim(),
          source: 'website',
          resource: resourceName,
          consent: true
        })
      });

      if (!res.ok) {
        const text = await res.text();
        let detail = 'Submission failed';
        try { detail = JSON.parse(text).detail || detail; } catch { /* non-JSON error response */ }
        throw new Error(detail);
      }

      const data = await res.json();
      const cid = data.contact_id;
      setContactId(cid);

      storeContact({ contact_id: cid, full_name: formData.full_name, email: formData.email, whatsapp: formData.whatsapp });

      // Record download
      await fetch(`${BACKEND_URL}/api/contacts/${cid}/download`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ resource: resourceName, source_page: window.location.pathname })
      });

      // Trigger download
      if (downloadUrl) {
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', '');
        link.setAttribute('target', '_blank');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      setStep('success');
      if (onSuccess) onSuccess(cid, data.profile);
    } catch (err) {
      toast.error(err.message || 'Something went wrong. Please try again.');
      setStep('form');
    }
  };

  // Auto-submit for returning visitors (they already have name/email/phone)
  const handleQuickDownload = useCallback(async () => {
    if (!contactId) return;
    setStep('submitting');
    try {
      // Just update the contact and record download
      await fetch(`${BACKEND_URL}/api/contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: formData.full_name, email: formData.email, whatsapp: formData.whatsapp,
          source: 'website', resource: resourceName, consent: true
        })
      });
      await fetch(`${BACKEND_URL}/api/contacts/${contactId}/download`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ resource: resourceName, source_page: window.location.pathname })
      });
      if (downloadUrl) {
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', '');
        link.setAttribute('target', '_blank');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      setStep('success');
      if (onSuccess) onSuccess(contactId);
    } catch {
      toast.error('Download failed. Please try again.');
      setStep('form');
    }
  }, [contactId, formData, resourceName, downloadUrl, onSuccess]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="lead-capture-popup">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#0b2e7f] to-[#1a4db3] px-6 py-5 text-white">
          <button onClick={onClose} className="absolute top-4 right-4 text-white/70 hover:text-white transition" data-testid="lead-popup-close">
            <X size={20} />
          </button>
          <div className="flex items-center gap-3 mb-1">
            <Download size={22} />
            <h3 className="text-lg font-semibold">Download {resourceName}</h3>
          </div>
          <p className="text-sm text-white/80">Enter your details to instantly access the resource and receive important ASEAN Business Forum 2026 updates.</p>
        </div>

        {step === 'form' && (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {isReturning && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800 mb-2">
                Welcome back, <strong>{formData.full_name}</strong>! 
                <button type="button" onClick={handleQuickDownload} className="ml-1 underline font-semibold hover:text-blue-900">
                  Quick download →
                </button>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name <span className="text-red-500">*</span></label>
              <input
                type="text" value={formData.full_name}
                onChange={e => setFormData({...formData, full_name: e.target.value})}
                className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition ${errors.full_name ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                placeholder="Your full name" data-testid="lead-name-input"
              />
              {errors.full_name && <p className="text-xs text-red-500 mt-1">{errors.full_name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email Address <span className="text-red-500">*</span></label>
              <input
                type="email" value={formData.email}
                onChange={e => setFormData({...formData, email: e.target.value})}
                className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition ${errors.email ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                placeholder="your@email.com" data-testid="lead-email-input"
              />
              {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp / Mobile <span className="text-red-500">*</span></label>
              <input
                type="tel" value={formData.whatsapp}
                onChange={e => setFormData({...formData, whatsapp: e.target.value})}
                className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition ${errors.whatsapp ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                placeholder="+62 812 3456 7890" data-testid="lead-whatsapp-input"
              />
              {errors.whatsapp && <p className="text-xs text-red-500 mt-1">{errors.whatsapp}</p>}
            </div>

            <Button type="submit" className="w-full bg-[#0b2e7f] hover:bg-[#1a4db3] text-white py-2.5 rounded-lg font-medium transition" data-testid="lead-submit-btn">
              <Download size={16} className="mr-2" /> Download Now
            </Button>

            <div className="flex items-center gap-2 text-xs text-gray-500 justify-center">
              <Shield size={12} />
              <span>We respect your privacy. No spam. Only summit updates.</span>
            </div>
          </form>
        )}

        {step === 'submitting' && (
          <div className="p-10 flex flex-col items-center justify-center">
            <Loader2 size={36} className="animate-spin text-[#0b2e7f] mb-3" />
            <p className="text-sm text-gray-600">Preparing your download...</p>
          </div>
        )}

        {step === 'success' && (
          <div className="p-8 text-center">
            <CheckCircle size={48} className="mx-auto text-green-500 mb-3" />
            <h4 className="text-lg font-semibold text-gray-900 mb-1">Your download has started!</h4>
            <p className="text-sm text-gray-500 mb-4">Thank you for your interest in the ASEAN Business Forum 2026.</p>
            <Button onClick={onClose} variant="outline" className="text-sm" data-testid="lead-done-btn">
              Close
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
